import { Component, ContentChildren, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, SimpleChanges, TemplateRef, OnChanges } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { isNil } from 'lodash-es';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { CdkColumnDef } from '@angular/cdk/table';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl } from '@angular/forms';
import { formatDate, KeyValue } from '@angular/common';
import { IComboFilterDataPair } from './combo-filters/combo-filters-data-pair';
import { MatSelectChange } from '@angular/material/select';


@Component({
  selector: 'datex-common-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss'],
  standalone: false
})
export class GridComponent implements OnInit, OnDestroy, OnChanges {

  private search$ = new Subject<string>();
  private searchSubscription: Subscription;
  private unsubscribe$ = new Subject();

  @Input()
  items: any[];
  @Input()
  totalCount: number;
  @Input()
  debounceSearchMs: number = 500;

  @Input() pageSize: number;
  @Input() pageSkip: number = 0;
  @Input() showDateFilter: boolean = false;
  @Input() rowClassFunc: (item: any) => string;
  @Output() pageChange: EventEmitter<void> = new EventEmitter();
  @Output() pageSizeChange: EventEmitter<number> = new EventEmitter();
  @Output() pageSkipChange: EventEmitter<number> = new EventEmitter();
  @Output() dateChange: EventEmitter<void> = new EventEmitter();
  @Output() dateFromChange: EventEmitter<Date> = new EventEmitter();
  @Output() dateToChange: EventEmitter<Date> = new EventEmitter();
  @Output() comboFilterChange: EventEmitter<KeyValue<string, IComboFilterDataPair>> = new EventEmitter();

  pagesCount = 0;
  currentPage = 1;
  currentPageFrom = 0;
  currentPageTo = 0;
  fullTextSearch: string;
  showOnlySelected = false;
  datePickerControlFrom = new FormControl();
  datePickerControlTo = new FormControl();

  @ContentChildren(CdkColumnDef, { descendants: true })
  columnDefs: QueryList<CdkColumnDef>;

  @Input()
  selected: any[];
  @Output()
  selectedChange = new EventEmitter<any[]>();

  @Output()
  performGetData = new EventEmitter<string>();

  @Input()
  isSameItemFunc: (item1: any, item2: any) => boolean;

  @Input()
  allowSelection = false;

  @Input()
  showSelectAll = false;

  @Input()
  showSelectedOnlyToggle = false;

  @Input()
  rowCommandTemplateRef: TemplateRef<any>;

  @Input()
  showFilter = true;

  @Input()
  fitContents = false;

  @Input()
  dateFrom = new Date(0);

  @Input()
  dateTo = new Date(0);

  @Input()
  comboFiltersMap: Map<string, IComboFilterDataPair[]>;

  get selectedCount() {
    return this.selected?.length;
  }

  get hasSelected() {
    return this.selectedCount > 0;
  }

  constructor(private snackBar: MatSnackBar) { }

  ngOnInit(): void {

    this.datePickerControlFrom.setValue(this.dateFrom);
    this.datePickerControlTo.setValue(this.dateTo);

    this.subscribeSearch();
  }

  // debounce search
  private subscribeSearch() {
    this.searchSubscription = this.search$
      .pipe(
        takeUntil(this.unsubscribe$),
        debounceTime(this.debounceSearchMs),
        distinctUntilChanged()
      )
      .subscribe(() => this.emitPerformGetData());
  }

  /**
   * Empties the search box without emitting performGetData — the caller reloads its own data.
   * The debounce subscription is rebuilt so that (a) a search still pending from before the clear
   * is dropped rather than re-applying the old filter (unsubscribing discards the debounced value;
   * completing the subject would flush it), and (b) re-typing the text that was just cleared still
   * triggers a search, which distinctUntilChanged would otherwise swallow.
   */
  clearSearch() {
    this.fullTextSearch = null;
    this.searchSubscription.unsubscribe();
    this.subscribeSearch();
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['totalCount'] || changes['pageSize'] || changes['pageSkip']) {
      this.updatePagination();
    }
    if (changes['items'] || changes['selected']) {
      this.updatePageSelectionState();
    }
  }

  onFilterInputChanged() {
    this.search$.next(this.fullTextSearch);
  }

  emitPerformGetData() {
    this.pageSkip = 0;
    this.pageSkipChange.emit(this.pageSkip);
    this.performGetData.emit(this.fullTextSearch);
  }

  isSelected(item) {
    return this.selected?.some(i => this.isSameItemFunc(i, item));
  }

  // memoized instead of getters: recomputing per change-detection cycle costs
  // O(page size × selection size) and these are bound in the header template
  allPageItemsSelected = false;
  somePageItemsSelected = false;

  onSelectAllChanged(event: MatCheckboxChange) {
    if (!this.items?.length) {
      return;
    }
    if (event.checked) {
      if (isNil(this.selected)) {
        this.selected = [];
      }
      this.items.forEach(item => {
        if (!this.isSelected(item)) {
          this.selected.push(item);
        }
      });
    } else {
      this.selected = this.selected?.filter(s => !this.items.some(item => this.isSameItemFunc(s, item))) ?? null;
    }
    this.emitSelectionChanged();
  }

  onCheckBoxChanged(event: MatCheckboxChange, item) {
    if (event.checked) {
      if (isNil(this.selected)) {
        this.selected = [];
      }
      this.selected.push(item);
    } else {
      const foundIndex = this.selected.findIndex(i => this.isSameItemFunc(i, item));
      this.selected.splice(foundIndex, 1);
    }
    this.emitSelectionChanged();
  }

  // single point that enforces the selected-is-null-when-empty convention
  private emitSelectionChanged() {
    if (this.selected?.length === 0) {
      this.selected = null;
    }
    this.updatePageSelectionState();
    this.selectedChange.emit(this.selected);
  }

  private updatePageSelectionState() {
    const allSelected = this.items?.length > 0 && this.items.every(item => this.isSelected(item));
    this.allPageItemsSelected = allSelected;
    this.somePageItemsSelected = !allSelected && !!this.items?.some(item => this.isSelected(item));
  }

  onShowOnlyToggleChange(event: MatSlideToggleChange) {
    this.showOnlySelected = event.checked;
    this.fullTextSearch = null;
    if (this.showOnlySelected) {
      this.items = this.selected;
    } else {
      this.emitPerformGetData();
    }
  }

  onClearSelectionClicked() {
    this.selected = null;
    this.emitSelectionChanged();
  }

  onValueChanged(dateType: string, event: MatDatepickerInputEvent<Date>) {

    if (!event.value)
      return;

    if (dateType === 'dateFrom') {

      let dateFromFormatted = formatDate(event.value, 'yyyy-MM-dd', 'en_US');
      let dateToFormatted = formatDate(this.dateTo, 'yyyy-MM-dd', 'en_US');

      if (this.dateTo && dateFromFormatted > dateToFormatted) {
        this.snackBar.open(`The date \"From\" cannot be greater than the date \"To\". No filtering will be applied.`, "OK", { duration: 5000 });
        this.datePickerControlFrom.setValue(this.dateFrom);
        return;
      }

      this.dateFrom = event.value;
      this.dateFromChange.emit(this.dateFrom);
    }

    if (dateType === 'dateTo') {

      let dateFromFormatted = formatDate(this.dateFrom, 'yyyy-MM-dd', 'en_US');
      let dateToFormatted = formatDate(event.value, 'yyyy-MM-dd', 'en_US');

      if (this.dateFrom && dateToFormatted < dateFromFormatted) {
        this.snackBar.open(`The date \"To\" cannot be lower than the date \"From\". No filtering will be applied.`, "OK", { duration: 5000 });
        this.datePickerControlTo.setValue(this.dateTo);
        return;
      }

      this.dateTo = event.value;
      this.dateToChange.emit(this.dateTo);
    }

    if (this.dateFrom && this.dateTo) {
      this.pageSkip = 0;
      this.pageSkipChange.emit(this.pageSkip);
      this.dateChange.emit();
    }
  }

  // Pagination
  goToPage(page: number) {
    if (page >= 1 && page <= this.pagesCount && page !== this.currentPage) {
      this.currentPage = page;
      this.updateCurrentPageFromTo();

      const pageSkip = (this.currentPage - 1) * this.pageSize;
      this.pageSkipChange.emit(pageSkip);
      this.pageChange.emit();
    }
  }

  updatePagination() {
    if (!isNil(this.pageSize) &&
      !isNil(this.totalCount) &&
      !isNil(this.pageSkip) &&
      this.pageSize > 0) {
      this.pagesCount = Math.ceil(this.totalCount / this.pageSize);
      this.currentPage = (this.pageSkip / this.pageSize) + 1;
    } else {
      this.pagesCount = 0;
      this.currentPage = 1;
    }
    this.updateCurrentPageFromTo();
  }

  updateCurrentPageFromTo() {
    if (this.pagesCount > 0) {
      this.currentPageFrom = (this.currentPage * this.pageSize) - this.pageSize + 1;
      if (this.currentPage !== this.pagesCount) {
        this.currentPageTo = this.currentPage * this.pageSize;
      } else {
        this.currentPageTo = this.totalCount;
      }
    } else {
      this.currentPageFrom = 0;
      this.currentPageTo = 0;
    }
  }

  isPositive(x: number) {
    return x !== null && x !== undefined && x > 0;
  }

  changeComboFilter(event: MatSelectChange) {
    let record: KeyValue<string, IComboFilterDataPair> = { key: event.source.id, value: event.value as IComboFilterDataPair };
    this.comboFilterChange.emit(record);
  }

}
